import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2 } from 'lucide-react';
import DurationInput from '../DurationInput.jsx';
import { apiLoggedTime } from '../../api/client.js';

/**
 * Sprint 24 — édition d'une saisie (LoggedTime) depuis l'onglet Time tracking
 * des Rapports. Réservée aux lignes `canEdit` (autorité serveur : propre Person
 * OU responsable d'affaire OU ADMIN). Modale centrée createPortal, fermeture
 * clic extérieur + ✕ + Échap. Gère le 423 (verrou comptable) → message dédié.
 *
 * Props :
 *   - row : { id, hours, notes, billable, personName, affaireName, taskName, date }
 *   - onClose() : fermeture sans changement
 *   - onSaved() : après succès (création/édition/suppression) → recharger
 */
export default function LoggedTimeEditModal({ row, onClose, onSaved }) {
  const [hours, setHours] = useState(Number(row.hours ?? 0));
  const [notes, setNotes] = useState(row.notes ?? '');
  const [billable, setBillable] = useState(Boolean(row.billable));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mapError = (e) => {
    if (e?.status === 423) return 'Période verrouillée : saisie non modifiable.';
    return e?.message || 'Erreur lors de l’enregistrement.';
  };

  const handleSave = async () => {
    if (!Number.isFinite(hours) || hours <= 0) {
      setError('Durée invalide (ex : 8, 8h30, 7.5).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiLoggedTime.update(row.id, { hours, notes: notes || null, billable });
      onSaved();
    } catch (e) {
      setError(mapError(e));
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette saisie ?')) return;
    setBusy(true);
    setError(null);
    try {
      await apiLoggedTime.archive(row.id);
      onSaved();
    } catch (e) {
      setError(mapError(e));
      setBusy(false);
    }
  };

  const label = { fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 };

  return createPortal(
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: 'min(440px, 92vw)',
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-card)',
          padding: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: 'var(--text)' }}>Modifier la saisie</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              {row.personName} · {row.affaireName} · {row.date}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fermer"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
          <div>
            <div style={label}>Heures</div>
            <DurationInput value={hours} onChange={setHours} />
          </div>
          <div>
            <div style={label}>Notes</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                resize: 'vertical',
                background: 'var(--glass)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text)',
                padding: '8px 10px',
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)' }}>
            <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
            Facturable
          </label>
        </div>

        {error && (
          <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 12 }}>{error}</div>
        )}

        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            title="Supprimer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--glass-border)',
              background: 'var(--glass)',
              color: 'var(--danger)',
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            <Trash2 size={15} />
            Supprimer
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={{
                padding: '8px 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--glass-border)',
                background: 'var(--glass)',
                color: 'var(--text)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              style={{
                padding: '8px 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--accent)',
                background: 'var(--accent)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
